import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Download, FileText, Package, Printer, Send, ArrowLeft,
  CheckCircle2, Loader2, AlertTriangle, RefreshCw, Eye, Info,
} from "lucide-react";
import { downloadFile } from "@/lib/downloadFile";
import PdfViewerModal from "@/components/wizard/PdfViewerModal";
import HandoffStatusCard from "@/components/wizard/HandoffStatusCard";
import AvidaLogo from "@/components/brand/AvidaLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const DOC_META = {
  pcst_f11133: { title: "PCST F-11133", subtitle: "Personal Care Screening Tool (11 pages)", route: "ForwardHealth · fax 608-221-8616" },
  f11136:      { title: "F-11136",      subtitle: "Personal Care Addendum (4 pages) · RN signed", route: "ForwardHealth · fax 608-221-8616" },
  f11134:      { title: "F-11134",      subtitle: "Provider Acknowledgement (1 page) · Agency Rep signed", route: "ForwardHealth · fax 608-221-8616" },
  cms485:      { title: "CMS-485",      subtitle: "Home Health Certification & Plan of Care (2 pages)", route: "Fax to physician for signature" },
  care_plan:   {
    title: "Client Care Plan · Preliminary",
    subtitle: "Plain-language plan (2-3 pages) · units from RN PCST calculation",
    route: "Print for client & PCW signatures — final plan issued after PA approval",
    badge: { label: "Preliminary · pending PA approval", tone: "yellow" },
  },
};

export default function DownloadCenter() {
  const { jobId } = useParams();
  const location = useLocation();
  const [state] = useState(location.state || null);
  const [busyAll, setBusyAll] = useState(false);
  const [busyOne, setBusyOne] = useState({});
  const [expired, setExpired] = useState(false);
  const [preview, setPreview] = useState(null); // { url, filename, title } | null

  // If this submission was kicked off from a parent-app visit (Stage 1
  // deep-link), the active-visit pointer is still in localStorage so the
  // HandoffStatusCard can render with the right visit_id for retries.
  const [activeVisitId] = useState(() => {
    try {
      const raw = window.localStorage.getItem("wi-pa-active-visit-v1");
      return raw ? (JSON.parse(raw) || {}).visit_id : null;
    } catch { return null; }
  });

  // Track PA outcome (approved/denied) pulled from the submission log
  // so the Download Center can (a) HIDE the preliminary Care Plan row
  // once the payer has ruled and (b) show an approved/denied badge card
  // pointing at the regenerated final PDF.
  const [paOutcome, setPaOutcome] = useState(null); // { outcome, details, final_pdf_filename }

  useEffect(() => {
    if (!jobId) return;
    axios.get(`${API}/submissions/${jobId}`).then(
      (res) => {
        setExpired(false);
        const po = res?.data?.pa_outcome;
        if (po && (po.outcome === "approved" || po.outcome === "denied")) {
          setPaOutcome(po);
        } else {
          setPaOutcome(null);
        }
      },
      (err) => { if (err?.response?.status === 404) setExpired(true); }
    );
  }, [jobId]);

  // Drop the preliminary care_plan row from the doc list once the PA
  // outcome is recorded. The Approved/Denied card below replaces it.
  const docs = (state?.documents || Object.keys(DOC_META).map((id) => ({
    doc_id: id, filename: `${DOC_META[id].title}.pdf`,
    download_url: `/api/wizard/${jobId}/doc/${id}`,
  }))).filter((d) => !(paOutcome && d.doc_id === "care_plan"));

  const [busyFinal, setBusyFinal] = useState(false);
  const downloadFinalCarePlan = async () => {
    setBusyFinal(true);
    try {
      const suffix = paOutcome?.outcome === "approved" ? "Final" : "Denied";
      await downloadFile(
        `${BACKEND}/api/wizard/${jobId}/final-care-plan`,
        `Client Care Plan - ${suffix}.pdf`,
      );
      toast.success(`${suffix} Care Plan downloaded`);
    } catch { /* toast handled */ }
    finally { setBusyFinal(false); }
  };

  const [busyReceipt, setBusyReceipt] = useState(false);
  const downloadPaReceipt = async () => {
    setBusyReceipt(true);
    try {
      const suffix = paOutcome?.outcome === "approved" ? "Approval" : "Denial";
      await downloadFile(
        `${BACKEND}/api/wizard/${jobId}/pa-receipt`,
        `PA ${suffix} Receipt.pdf`,
      );
      toast.success(`PA ${suffix} Receipt downloaded`);
    } catch { /* toast handled */ }
    finally { setBusyReceipt(false); }
  };

  const openPreview = (d) => {
    setPreview({
      url: `${BACKEND}${d.download_url}`,
      filename: d.filename,
      title: DOC_META[d.doc_id]?.title || d.filename,
    });
  };

  const downloadOne = async (doc) => {
    setBusyOne((m) => ({ ...m, [doc.doc_id]: true }));
    try {
      await downloadFile(`${BACKEND}${doc.download_url}`, doc.filename);
      toast.success(`Saved ${doc.filename}`);
    } catch { /* toast handled */ }
    finally { setBusyOne((m) => ({ ...m, [doc.doc_id]: false })); }
  };

  const downloadBundle = async () => {
    setBusyAll(true);
    try {
      await downloadFile(`${BACKEND}/api/wizard/${jobId}/bundle`, `PA_Bundle_${jobId}.zip`);
      toast.success("Bundle downloaded ✨");
    } catch { /* toast handled */ }
    finally { setBusyAll(false); }
  };

  const [busyPacket, setBusyPacket] = useState(false);
  const downloadPacket = async () => {
    setBusyPacket(true);
    try {
      await downloadFile(
        `${BACKEND}/api/wizard/${jobId}/packet`,
        `Submission_Packet_${jobId}.pdf`,
      );
      toast.success("Print-ready packet downloaded");
    } catch { /* toast handled */ }
    finally { setBusyPacket(false); }
  };

  const [busyFhir, setBusyFhir] = useState(false);
  const downloadFhirBundle = async () => {
    setBusyFhir(true);
    try {
      const res = await fetch(`${BACKEND}/api/wizard/${jobId}/fhir-bundle`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const bundle = await res.json();
      const blob = new Blob([JSON.stringify(bundle, null, 2)], {
        type: "application/fhir+json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `FHIR_Bundle_${jobId}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(
        `FHIR R4 Bundle downloaded · ${bundle?.entry?.length || 0} resources`,
      );
    } catch (err) {
      toast.error(err?.message || "Could not export FHIR Bundle");
    }
    finally { setBusyFhir(false); }
  };

  return (
    <main className="min-h-screen bg-paper" data-testid="download-center">
      <div className="brand-strip-thin border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="download-logo">
            <div className="rounded-lg bg-white p-1 border border-white/20">
              <AvidaLogo size="xs" iconOnly />
            </div>
            <div className="pl-3 border-l border-white/20">
              <div className="font-display text-[14px] font-semibold text-white">Avida Health</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#FFDA0B] font-bold mt-0.5">Submission Center</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/wizard/10" className="btn-ghost" data-testid="download-back">
              <ArrowLeft className="w-4 h-4" /> Back to wizard
            </Link>
            <Link to="/submissions" className="btn-ghost" data-testid="download-log">
              Submission log
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center bg-[#FFDA0B]">
            <CheckCircle2 className="w-6 h-6 text-[#072A54]" strokeWidth={2.3} />
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0B1E3A] tracking-tight">
            Submission package <span className="yellow-underline">ready</span>
          </h1>
        </div>
        <p className="text-[#3A5275] mb-6 max-w-2xl mt-3">
          All prior-authorization documents have been generated. Tap <span className="font-semibold">Preview</span> on any row
          to view the PDF right here in the app — no download needed. If you're inside a sandboxed preview
          and downloads are blocked, Preview + Print still works from the viewer.
        </p>

        <div className="mb-8 rounded-2xl border border-[#B7CADA] bg-[#E7F1FB] p-4 flex items-start gap-3" data-testid="download-help-banner">
          <Info className="w-5 h-5 text-[#0A3D79] mt-0.5 shrink-0" />
          <div className="text-[13px] text-[#0B1E3A]">
            <span className="font-semibold">Downloads not starting?</span> The Emergent preview iframe may block file saves.
            Tap <span className="font-semibold">Preview</span> below to render the PDF inline, then use the viewer's
            <span className="font-semibold"> Save</span> or <span className="font-semibold">Print</span> buttons — those work everywhere.
          </div>
        </div>

        {/* Stage 3 handoff status — only renders when a parent-app visit
            was attached to this submission, so the RN sees confirmation
            (or a retry button) for the bundle that ships off-platform. */}
        <HandoffStatusCard jobId={jobId} visitId={activeVisitId} />

        {expired && (
          <div className="mb-8 rounded-2xl border border-[#e2b6a9] bg-[#fbeae5] p-4 flex items-start gap-3" data-testid="download-expired-banner">
            <AlertTriangle className="w-5 h-5 text-[#8a2e1a] mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-[#8a2e1a]">This job has expired</div>
              <div className="text-[13px] text-[#3A5275] mt-1">
                Re-run the wizard (your signed draft is still saved on this device) to regenerate all 5 documents.
              </div>
              <Link to="/wizard/10" className="btn-primary mt-3 inline-flex" data-testid="download-expired-regenerate">
                <RefreshCw className="w-4 h-4" /> Regenerate PDFs
              </Link>
            </div>
          </div>
        )}

        <div className="card-surface mb-4 bg-gradient-to-b from-[#FFFDEC] to-[#FFF3B3] border-[#DBB90A]" data-testid="packet-card">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#8A6B00]">
                Recommended · Print-ready
              </div>
              <div className="font-display text-xl font-semibold text-[#072A54] mt-0.5">
                One-file Submission Packet
              </div>
              <div className="text-[13px] text-[#3A5275] mt-1">
                Cover page · DHS compliance badge · all 7 PDFs merged with clickable bookmarks.
              </div>
            </div>
            <button
              type="button"
              onClick={downloadPacket}
              disabled={busyPacket || expired}
              className="btn-accent disabled:opacity-60"
              data-testid="download-packet"
            >
              {busyPacket ? <><Loader2 className="w-4 h-4 animate-spin" /> Downloading…</>
                          : <><FileText className="w-4 h-4" /> Download packet PDF</>}
            </button>
          </div>
        </div>

        <div className="card-surface mb-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#3A5275]">Complete bundle</div>
              <div className="font-display text-xl font-semibold text-[#0B1E3A] mt-0.5">All 7 PDFs · ZIP archive</div>
              <div className="text-[13px] text-[#3A5275] mt-1">Separate files for uploading to individual portals.</div>
            </div>
            <button
              type="button"
              onClick={downloadBundle}
              disabled={busyAll || expired}
              className="btn-primary disabled:opacity-60"
              data-testid="download-bundle"
            >
              {busyAll ? <><Loader2 className="w-4 h-4 animate-spin" /> Downloading…</>
                        : <><Package className="w-4 h-4" /> Download ZIP bundle</>}
            </button>
          </div>
        </div>

        <div className="card-surface mb-8" data-testid="fhir-card">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#3A5275]">EHR / HL7 ingest</div>
              <div className="font-display text-xl font-semibold text-[#0B1E3A] mt-0.5">FHIR R4 Bundle · JSON</div>
              <div className="text-[13px] text-[#3A5275] mt-1">
                Patient · Practitioner · Organization · Conditions · Medications · ServiceRequest · DocumentReferences.
                Drop into any FHIR-compliant EHR or the parent app's HL7 gateway.
              </div>
            </div>
            <button
              type="button"
              onClick={downloadFhirBundle}
              disabled={busyFhir || expired}
              className="btn-ghost disabled:opacity-60"
              data-testid="download-fhir-bundle"
              title="Export the visit as a FHIR R4 Bundle (.json)"
            >
              {busyFhir ? <><Loader2 className="w-4 h-4 animate-spin" /> Exporting…</>
                        : <><Download className="w-4 h-4" /> Download FHIR Bundle</>}
            </button>
          </div>
        </div>

        <div className="space-y-3 mb-10">
          {paOutcome && paOutcome.outcome === "approved" && (
            <div
              className="card-surface flex items-start justify-between flex-wrap gap-4 border-[#3a8f5a] bg-[#F4FAF6]"
              data-testid="final-care-plan-card"
            >
              <div className="flex items-start gap-4">
                <FileText className="w-6 h-6 text-[#1e5a36] mt-0.5" />
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display text-[16px] font-semibold text-[#0B1E3A]">
                      Client Care Plan · Approved
                    </div>
                    <span
                      className="inline-flex items-center rounded-full bg-[#E6F5EC] border border-[#3a8f5a] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#1e5a36]"
                      data-testid="final-care-plan-badge"
                    >
                      PA Approved · Final
                    </span>
                  </div>
                  <div className="text-[13px] text-[#3A5275]">
                    ForwardHealth authorized
                    {paOutcome?.details?.approval_date ? ` on ${paOutcome.details.approval_date}` : ""}
                    {paOutcome?.details?.pa_number ? ` · PA #${paOutcome.details.pa_number}` : ""}
                    {paOutcome?.details?.approved_units_per_week
                      ? ` · ${paOutcome.details.approved_units_per_week} units/week`
                      : ""}
                  </div>
                  <div className="text-[12px] text-[#3a5b4d] font-semibold mt-1">
                    Print for client &amp; PCW signatures · this is the payer-authorized plan
                  </div>
                  {(paOutcome?.avida_synced || paOutcome?.avida_sync_failed) && (
                    <div
                      className={`mt-1 inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-0.5 rounded-full border ${
                        paOutcome.avida_synced
                          ? "border-[#3a8f5a] bg-[#E6F5EC] text-[#1e5a36]"
                          : "border-[#C05746] bg-[#FBEAE5] text-[#8A2E1A]"
                      }`}
                      data-testid="avida-sync-status-approved"
                      title={paOutcome.avida_last_error || ""}
                    >
                      {paOutcome.avida_synced ? "Synced to Avida EHR" : "Avida sync failed"}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={downloadFinalCarePlan}
                  disabled={busyFinal || expired}
                  className="btn-primary disabled:opacity-60"
                  data-testid="download-final-care-plan"
                >
                  {busyFinal ? <><Loader2 className="w-4 h-4 animate-spin" /> Downloading…</>
                            : <><Download className="w-4 h-4" /> Download Final Care Plan</>}
                </button>
                <button
                  type="button"
                  onClick={downloadPaReceipt}
                  disabled={busyReceipt || expired}
                  className="btn-ghost disabled:opacity-60"
                  data-testid="download-approval-receipt"
                  title="1-page audit artifact: PA #, units, cert period, approved activities, recorded by + timestamp"
                >
                  {busyReceipt ? <><Loader2 className="w-4 h-4 animate-spin" /> Downloading…</>
                              : <><FileText className="w-4 h-4" /> Approval Receipt</>}
                </button>
              </div>
            </div>
          )}
          {paOutcome && paOutcome.outcome === "denied" && (
            <div
              className="card-surface flex items-start justify-between flex-wrap gap-4 border-[#C05746] bg-[#FDF5F3]"
              data-testid="denied-care-plan-card"
            >
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-6 h-6 text-[#8A2E1A] mt-0.5" />
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display text-[16px] font-semibold text-[#0B1E3A]">
                      Client Care Plan · Denied
                    </div>
                    <span
                      className="inline-flex items-center rounded-full bg-[#FBEAE5] border border-[#C05746] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#8A2E1A]"
                      data-testid="denied-care-plan-badge"
                    >
                      PA Denied · Do Not Distribute
                    </span>
                  </div>
                  <div className="text-[13px] text-[#3A5275]">
                    ForwardHealth denied PA
                    {paOutcome?.details?.denial_date ? ` on ${paOutcome.details.denial_date}` : ""}.
                    Reason: {paOutcome?.details?.denial_reason || "not provided"}.
                  </div>
                  <div className="text-[12px] text-[#8A2E1A] font-semibold mt-1">
                    Do NOT distribute to client or PCW — RN must correct the submission and resubmit.
                  </div>
                  {(paOutcome?.avida_synced || paOutcome?.avida_sync_failed) && (
                    <div
                      className={`mt-1 inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-0.5 rounded-full border ${
                        paOutcome.avida_synced
                          ? "border-[#3a8f5a] bg-[#E6F5EC] text-[#1e5a36]"
                          : "border-[#C05746] bg-[#FBEAE5] text-[#8A2E1A]"
                      }`}
                      data-testid="avida-sync-status-denied"
                      title={paOutcome.avida_last_error || ""}
                    >
                      {paOutcome.avida_synced ? "Synced to Avida EHR" : "Avida sync failed"}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={downloadFinalCarePlan}
                  disabled={busyFinal || expired}
                  className="btn-ghost disabled:opacity-60"
                  data-testid="download-denied-care-plan"
                  title="Download the denial-stamped Care Plan for the agency record"
                >
                  {busyFinal ? <><Loader2 className="w-4 h-4 animate-spin" /> Downloading…</>
                            : <><Download className="w-4 h-4" /> Download for agency record</>}
                </button>
                <button
                  type="button"
                  onClick={downloadPaReceipt}
                  disabled={busyReceipt || expired}
                  className="btn-ghost disabled:opacity-60"
                  data-testid="download-denial-receipt"
                  title="1-page audit artifact: denial date, denial reason, resubmission guidance, recorded by + timestamp"
                >
                  {busyReceipt ? <><Loader2 className="w-4 h-4 animate-spin" /> Downloading…</>
                              : <><FileText className="w-4 h-4" /> Denial Receipt</>}
                </button>
              </div>
            </div>
          )}
          {docs.map((d) => {
            const meta = DOC_META[d.doc_id];
            const busy = !!busyOne[d.doc_id];
            return (
              <div key={d.doc_id} className="card-surface flex items-center justify-between flex-wrap gap-4" data-testid={`doc-${d.doc_id}`}>
                <div className="flex items-start gap-4">
                  <FileText className="w-6 h-6 text-[#0A3D79] mt-0.5" />
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-display text-[16px] font-semibold text-[#0B1E3A]">{meta?.title}</div>
                      {meta?.badge && (
                        <span
                          className={
                            meta.badge.tone === "yellow"
                              ? "inline-flex items-center rounded-full bg-[#FFFDEC] border border-[#DBB90A] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#8A6B00]"
                              : "inline-flex items-center rounded-full bg-[#E6F5EC] border border-[#3a8f5a] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#1e5a36]"
                          }
                          data-testid={`doc-badge-${d.doc_id}`}
                        >
                          {meta.badge.label}
                        </span>
                      )}
                    </div>
                    <div className="text-[13px] text-[#3A5275]">{meta?.subtitle}</div>
                    <div className="text-[12px] text-[#3a5b4d] font-semibold mt-1">{meta?.route}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => openPreview(d)}
                    disabled={expired}
                    className="btn-primary disabled:opacity-60"
                    data-testid={`doc-preview-${d.doc_id}`}
                  >
                    <Eye className="w-4 h-4" /> Preview
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadOne(d)}
                    disabled={busy || expired}
                    className="btn-ghost disabled:opacity-60"
                    data-testid={`doc-download-${d.doc_id}`}
                    title="Save the PDF to your device"
                  >
                    {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> …</>
                          : <><Download className="w-4 h-4" /> Save</>}
                  </button>
                  <a
                    href={`${BACKEND}${d.download_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-ghost"
                    data-testid={`doc-open-${d.doc_id}`}
                    title="Open the PDF in a standalone tab"
                  >
                    New tab
                  </a>
                </div>
              </div>
            );
          })}
        </div>

        <div className="card-surface">
          <h2 className="font-display text-xl font-semibold text-[#0B1E3A] mb-3">Next steps — routing</h2>
          <ul className="space-y-3 text-[14px] text-[#0B1E3A]">
            <li className="flex items-start gap-3">
              <Send className="w-5 h-5 text-[#0A3D79] flex-shrink-0 mt-0.5" />
              <span><span className="font-semibold">ForwardHealth</span> — fax PCST F-11133 + F-11136 + F-11134 to <span className="font-mono font-semibold">608-221-8616</span>.</span>
            </li>
            <li className="flex items-start gap-3">
              <Send className="w-5 h-5 text-[#0A3D79] flex-shrink-0 mt-0.5" />
              <span><span className="font-semibold">Physician</span> — fax the CMS-485 to the attending physician for wet signature, then file returned signed copy with the agency record.</span>
            </li>
            <li className="flex items-start gap-3">
              <Printer className="w-5 h-5 text-[#0A3D79] flex-shrink-0 mt-0.5" />
              <span>
                <span className="font-semibold">Client &amp; PCW</span> — print the
                <span className="font-semibold"> Preliminary</span> Client Care Plan and
                obtain client and personal care worker signatures during the next visit.
                A <span className="font-semibold">Final Care Plan</span> (with payer-authorized
                units) will be issued once ForwardHealth approves the PA.
              </span>
            </li>
          </ul>
        </div>
      </div>

      {preview && (
        <PdfViewerModal
          url={preview.url}
          filename={preview.filename}
          title={preview.title}
          onClose={() => setPreview(null)}
        />
      )}
    </main>
  );
}
